import React, { useEffect, useState } from "react";
import {
  message,
  Row,
  Col,
  Table,
  Spin,
  Space,
  Select,
  Card,
  Tag,
  Button,
  Typography,
} from "antd";
import { SyncOutlined, FileTextOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import { HEADERS_EXPORT_BEN, POST_MAIN_DTR_PAYMENT } from "../../API/apiUrls";
import ListDataPayment from "./modal/ListDataPayment";

const { Option } = Select;
const { Text } = Typography;

// ── flattenLoan ─────────────────────────────────────────────────
const flattenLoan = (loan) => {
  const persons = loan.data || [];
  const firstPerson = persons[0] || {};

  const totalNew = persons.reduce((s, r) => {
    return (
      s +
      Number(r.money?.additionalFee || 0) +
      Number(r.money?.familyRegistration || 0) +
      Number(r.money?.operationFee || 0) +
      Number(r.money?.spouseFee || 0) +
      Number(r.money?.tr14 || 0)
    );
  }, 0);

  if (!totalNew) return null;

  return {
    loanId: loan.loanId,
    invRoundId: firstPerson.invRoundId,
    invId: firstPerson.invId,
    NETAMT: totalNew,
    CONTNO: loan.contno,
    SCHEMA: loan.SCHEMA,
    CUSTNAME: `${loan.SNAM}${loan.NAME1} ${loan.NAME2}`.trim(),
    responseDateInvestigate: loan.responseDateInvestigate,
    dtrPaymentId: loan.dtrPaymentId,
    flagSendTempbill: loan.flagSendTempbill,
    _raw: loan,
  };
};

// ── groupByDate ─────────────────────────────────────────────────
const groupByDate = (rows) => {
  const map = new Map();

  rows.forEach((row) => {
    const dateKey = dayjs(row.responseDateInvestigate).format("YYYY-MM-DD");
    if (!map.has(dateKey)) map.set(dateKey, []);
    map.get(dateKey).push(row);
  });

  const result = [];
  map.forEach((loans, dateKey) => {
    result.push({
      key: dateKey,
      dateKey,
      loans,
      totalContracts: loans.length,
      totalNETAMT: loans.reduce((s, r) => s + r.NETAMT, 0),
      rawData: loans.map((r) => r._raw), // ← เก็บ loan ต้นฉบับทุกตัวของวันนี้
    });
  });

  return result.sort(
    (a, b) => dayjs(b.dateKey).valueOf() - dayjs(a.dateKey).valueOf(),
  );
};

// ────────────────────────────────────────────────────────────────
const Main = () => {
  const [loading, setLoading] = useState(false);
  const [dateRows, setDateRows] = useState([]); // rows หลัก (จัดกลุ่มวันที่)
  const [flatCache, setFlatCache] = useState([]); // flat rows ต้นฉบับ (ใช้ filter)
  const [searchSelect, setSearchSelect] = useState(1);
  const [isModal, setIsModal] = useState(false);
  const [dataRecord, setDataRecord] = useState();

  useEffect(() => {
    loadRoundData();
  }, [searchSelect]);

  const loadRoundData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_MAIN_DTR_PAYMENT,
        { flagSearch: searchSelect },
        { headers: HEADERS_EXPORT_BEN },
      );

      if (res.status === 200) {
        if (
          res.data.message === "ไม่พบข้อมูล" ||
          res.data.success === "ไม่พบข้อมูล"
        ) {
          message.warning("ไม่พบข้อมูลในระบบ");
          setDateRows([]);
          setFlatCache([]);
          return;
        }

        const flat = res?.data?.map(flattenLoan).filter(Boolean);
        console.log("flat", flat);

        setFlatCache(flat);
        setDateRows(groupByDate(flat));
      } else {
        message.error("ดึงข้อมูลไม่สำเร็จ");
        setDateRows([]);
        setFlatCache([]);
      }
    } catch (error) {
      console.error(error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSelect = (value) => {
    setSearchSelect(value);
    const filtered =
      value === 99
        ? flatCache
        : flatCache.filter((r) => r.flagSendTempbill === value);
    setDateRows(groupByDate(filtered));
  };

  const handleApproval = (record) => {
    console.log(record.rawData);
    setDataRecord(record.rawData);
    setIsModal(true);
  };

  const SCHEMA_GROUP = {
    sfhp: "group1",
    vsfhp: "group2",
    lsfhp: "group3",
    rpsl: "group3",
    psfhp: "group3",
  };

  const GROUP_LABEL = {
    group1: {
      label: "บัญชี V,8",
      color: "#185FA5",
      bg: "#E6F1FB",
      textColor: "#0C447C",
    },
    group2: {
      label: "บัญชี 2 ",
      color: "#0F6E56",
      bg: "#E1F5EE",
      textColor: "#085041",
    },
    group3: {
      label: "บัญชี 1, P-LOAN, 3 เก่า",
      color: "#854F0B",
      bg: "#FAEEDA",
      textColor: "#633806",
    },
  };
  // ── ตารางภายใน expand ────────────────────────────────────────
  const innerColumns = [
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      width: 130,
    },
    {
      title: "ชื่อลูกค้า",
      dataIndex: "CUSTNAME",
    },
    {
      title: "บัญชี",
      dataIndex: "SCHEMA",
      align: "center",
      width: 110,
      render: (schema) => {
        const grp = SCHEMA_GROUP[schema];
        const info = GROUP_LABEL[grp];
        if (!info) return <Tag>{schema}</Tag>;
        return (
          <Tag
            style={{
              backgroundColor: info.bg,
              color: info.textColor,
              border: `1px solid ${info.color}`,
              fontWeight: 600,
              minWidth: 80,
              textAlign: "center",
            }}
          >
            {info.label}
          </Tag>
        );
      },
    },
    {
      title: "ยอดรวม",
      dataIndex: "NETAMT",
      align: "right",
      width: 120,
      render: (val) =>
        Number(val).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
  ];

  // ── ตารางหลัก (1 แถว = 1 วันที่) ────────────────────────────
  const outerColumns = [
    {
      title: "วันที่",
      dataIndex: "dateKey",
      align: "center",
      width: 140,
      render: (text) => <Text strong>{dayjs(text).format("DD MMM YYYY")}</Text>,
    },
    {
      title: "จำนวนสัญญา",
      dataIndex: "totalContracts",
      align: "center",
      width: 120,
      render: (val) => (
        <Tag color="blue" style={{ minWidth: 50, textAlign: "center" }}>
          {val} สัญญา
        </Tag>
      ),
    },
    {
      title: "ยอดรวมทั้งหมด",
      dataIndex: "totalNETAMT",
      align: "right",
      width: 150,
      render: (val) => (
        <Text strong>
          {Number(val).toLocaleString("th-TH", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      ),
    },
    {
      title: "สถานะ",
      align: "center",
      width: 110,
      render: () => (
        <Tag
          color={searchSelect === 2 ? "success" : "warning"}
          style={{ minWidth: 70, textAlign: "center" }}
        >
          {searchSelect === 2 ? "ส่งแล้ว" : "ยังไม่ส่ง"}
        </Tag>
      ),
    },
    {
      title: "การจัดการ",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Button
          variant="outlined"
          color={searchSelect === 2 ? "green" : "blue"}
          icon={<FileTextOutlined />}
          onClick={() => handleApproval(record)}
          size="small"
          style={{ minWidth: 110, borderRadius: 8 }}
        >
          {searchSelect === 2 ? "ดูข้อมูล" : "อนุมัติบิล"}
        </Button>
      ),
    },
  ];

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ display: "flex", width: "100%" }}
    >
      {/* ── ตัวกรอง ── */}
      <Card style={{ padding: "16px 24px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size={16}>
              <Text strong style={{ fontSize: 16 }}>
                ตัวกรองสถานะ:
              </Text>
              <Select
                size="large"
                style={{ width: 180 }}
                value={searchSelect}
                onChange={handleSearchSelect}
              >
                <Option value={1}>📑 ยังไม่ตรวจ</Option>
                <Option value={2}>✅ ตรวจแล้ว</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              size="large"
              onClick={loadRoundData}
              loading={loading}
            >
              รีเฟรชข้อมูล
            </Button>
          </Col>
        </Row>
      </Card>

      {/* ── ตารางหลัก ── */}
      <Card style={{ overflow: "hidden", padding: 0 }}>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Table
            size="middle"
            columns={outerColumns}
            dataSource={dateRows}
            rowKey="key"
            scroll={{ x: 700 }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `ทั้งหมด ${total} วันที่`,
            }}
            expandable={{
              // expandRowByClick: true,
              expandedRowRender: (record) => (
                <Table
                  size="small"
                  columns={innerColumns}
                  dataSource={record.loans}
                  rowKey="loanId"
                  pagination={false}
                  style={{ margin: "8px 0" }}
                />
              ),
            }}
            footer={() => (
              <div style={{ textAlign: "right", paddingRight: 16 }}>
                <Text strong>จำนวนวันที่ทั้งหมด: </Text>
                <Text type="danger" strong>
                  {dateRows.length}
                </Text>
                <Text strong style={{ marginLeft: 24 }}>
                  จำนวนสัญญาทั้งหมด:{" "}
                </Text>
                <Text type="danger" strong>
                  {dateRows.reduce((s, r) => s + r.totalContracts, 0)}
                </Text>
              </div>
            )}
          />
        </Spin>

        {isModal && (
          <ListDataPayment
            open={isModal}
            close={setIsModal}
            dataLoad={dataRecord}
            func={(v) => {
              if (v) loadRoundData();
            }}
            status={searchSelect}
          />
        )}
      </Card>
    </Space>
  );
};

const MainPaymentInvestigate = MotionHoc(Main);
export default MainPaymentInvestigate;
